/**
 * Package Change Dialog (Epic 3 — Toewijzing met tijdspercentages en
 * tijdvakken, Story 3.2)
 *
 * Eén-stap dialoog (UX-spec S3): opent altijd een NIEUW tijdvak (pakket-
 * wissel — AD-2/FR-5), sluit nooit een bestaand tijdvak in-place. Dit
 * project heeft geen shadcn/ui (zie `CoreTasksTab.tsx` Dev Notes) — Dialog
 * is een custom modal (overlay + `useEscapeToClose`-patroon), Combobox is
 * een doorzoekbare filter-lijst (`matchesSearch`, zelfde patroon als
 * `CoreTasksTab.tsx`'s domain-groepering), Tooltip is `HoverTooltip`.
 *
 * AD-2 (bindend): de server is autoritatief voor alle spelregels — dit
 * component vertoont de regels vóór (stepper 10-100/stap 10, maand-only
 * picker) maar herhaalt ze niet als enige bron van waarheid.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { matchesSearch } from '../../../lib/textUtils';
import { formatDutchDate } from '../../../lib/dateUtils';
import { useCoreTasks } from '../../settings/useCoreTasks';
import { useSaveTaskPeriod } from '../useAssignmentPeriods';
import { ApiRequestError } from '../../../lib/api';
import type { AssignmentPeriodDTO, CoreTaskDTO } from '@functieloonhuis/shared';

const MAX_PERCENTAGE = 100;
const MIN_PERCENTAGE = 10;
const STEP = 10;
const DEFAULT_ROW_COUNT = 3;

interface PackageRow {
  key: number;
  coreTaskId: number | null;
  timePercentage: number;
}

let rowKeyCounter = 0;
function newRow(): PackageRow {
  return { key: rowKeyCounter++, coreTaskId: null, timePercentage: MIN_PERCENTAGE };
}

/** Genereer maand-opties (1e van de maand): 12 maanden terug t/m 12 maanden vooruit. */
function generateMonthOptions(): string[] {
  const options: string[] = [];
  const now = new Date();
  for (let offset = -12; offset <= 12; offset++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
    const iso = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
    options.push(iso);
  }
  return options;
}

/** Laatste dag van de maand vóór `iso` (voor de infozin "loopt dan t/m ..."). */
function lastDayOfPreviousMonth(iso: string): string {
  const [year, month] = iso.split('-').map(Number);
  const prevDate = new Date(Date.UTC(year!, month! - 1, 0));
  return `${prevDate.getUTCFullYear()}-${String(prevDate.getUTCMonth() + 1).padStart(2, '0')}-${String(prevDate.getUTCDate()).padStart(2, '0')}`;
}

function nextMonthIso(): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

interface CoreTaskPickerProps {
  tasks: CoreTaskDTO[];
  value: number | null;
  onChange: (coreTaskId: number) => void;
  excludeIds: number[];
  testId: string;
}

function CoreTaskPicker({ tasks, value, onChange, excludeIds, testId }: CoreTaskPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  const selected = tasks.find((t) => t.id === value) ?? null;

  const groups = useMemo(() => {
    // Defensief: ook al filtert de backend `GET /core-tasks` standaard
    // gearchiveerde kerntaken al uit, herhaal die check hier zodat de
    // picker nooit een gearchiveerde kerntaak toont ongeacht hoe de hook
    // wordt aangeroepen (AD-2: UI mag regels wel vóórtonen, maar moet ze
    // ook zelf niet per ongeluk schenden).
    const available = tasks.filter((t) => t.isActive && (!excludeIds.includes(t.id) || t.id === value));
    const filtered = available.filter((t) => matchesSearch(t.name, query) || matchesSearch(t.domain, query));
    const byDomain = new Map<string, CoreTaskDTO[]>();
    const domainOrder: string[] = [];
    for (const task of filtered) {
      if (!byDomain.has(task.domain)) {
        byDomain.set(task.domain, []);
        domainOrder.push(task.domain);
      }
      byDomain.get(task.domain)!.push(task);
    }
    return domainOrder.map((domain) => ({ domain, tasks: byDomain.get(domain)! }));
  }, [tasks, excludeIds, value, query]);

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white hover:border-[#54949E] focus:outline-none focus:ring-2 focus:ring-[#54949E]"
      >
        {selected ? selected.name : <span className="text-gray-400">Kies een kerntaak…</span>}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-[#E2E8F0] rounded-md shadow-lg max-h-64 overflow-y-auto">
          <input
            type="text"
            autoFocus
            data-testid={`${testId}-search`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoeken…"
            className="w-full px-3 py-2 text-sm border-b border-[#E2E8F0] focus:outline-none"
          />
          {groups.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Geen kerntaken gevonden</p>}
          {groups.map((group) => (
            <div key={group.domain}>
              <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50">{group.domain}</div>
              {group.tasks.map((task) => (
                <button
                  key={task.id}
                  type="button"
                  data-testid={`${testId}-option-${task.id}`}
                  onClick={() => {
                    onChange(task.id);
                    setQuery('');
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                >
                  {task.name}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface PercentageStepperProps {
  value: number;
  onChange: (value: number) => void;
  testId: string;
}

function PercentageStepper({ value, onChange, testId }: PercentageStepperProps) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <button
        type="button"
        data-testid={`${testId}-decrement`}
        disabled={value <= MIN_PERCENTAGE}
        onClick={() => onChange(Math.max(MIN_PERCENTAGE, value - STEP))}
        className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E2E8F0] text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        aria-label="Verlagen"
      >
        −
      </button>
      <span data-testid={`${testId}-value`} className="w-12 text-center text-sm font-medium text-gray-900">
        {value}%
      </span>
      <button
        type="button"
        data-testid={`${testId}-increment`}
        disabled={value >= MAX_PERCENTAGE}
        onClick={() => onChange(Math.min(MAX_PERCENTAGE, value + STEP))}
        className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E2E8F0] text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        aria-label="Verhogen"
      >
        +
      </button>
    </div>
  );
}

interface PackageChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  currentPackage: AssignmentPeriodDTO | null;
  onSaved?: () => void;
}

export function PackageChangeDialog({ isOpen, onClose, employeeId, currentPackage, onSaved }: PackageChangeDialogProps) {
  const { data: coreTasks } = useCoreTasks(false);
  const saveTaskPeriod = useSaveTaskPeriod(employeeId);

  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const [startDate, setStartDate] = useState<string>(nextMonthIso());
  const [rows, setRows] = useState<PackageRow[]>(() => [newRow(), newRow(), newRow()]);
  const [exceptionMotivation, setExceptionMotivation] = useState('');
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStartDate(nextMonthIso());
    setRows([newRow(), newRow(), newRow()]);
    setExceptionMotivation('');
    setSaveAttempted(false);
    setServerError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sum = rows.reduce((total, row) => total + row.timePercentage, 0);
  const sumIsValid = sum === MAX_PERCENTAGE;
  const hasException = rows.length > DEFAULT_ROW_COUNT;
  const exceptionMotivationValid = !hasException || exceptionMotivation.trim().length > 0;
  const allRowsHaveTask = rows.every((row) => row.coreTaskId !== null);
  const isValid = sumIsValid && allRowsHaveTask && exceptionMotivationValid;

  function missingReasons(): string[] {
    const reasons: string[] = [];
    if (!allRowsHaveTask) reasons.push('Kies een kerntaak voor elke regel');
    if (!sumIsValid) reasons.push(sum < MAX_PERCENTAGE ? `Nog ${MAX_PERCENTAGE - sum}% te verdelen` : `${sum - MAX_PERCENTAGE}% te veel`);
    if (!exceptionMotivationValid) reasons.push('Motivatie is verplicht voor de 4e/5e kerntaak');
    return reasons;
  }

  const excludeIds = rows.map((r) => r.coreTaskId).filter((id): id is number => id !== null);

  function updateRow(key: number, patch: Partial<PackageRow>) {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function addExceptionRow() {
    setRows((prev) => [...prev, newRow()]);
  }

  async function handleSave() {
    setSaveAttempted(true);
    setServerError(null);
    if (!isValid) return;

    try {
      await saveTaskPeriod.mutateAsync({
        input: {
          startDate,
          endDate: null,
          exceptionMotivation: hasException ? exceptionMotivation.trim() : null,
          assignments: rows.map((row) => ({ coreTaskId: row.coreTaskId!, timePercentage: row.timePercentage })),
        },
      });
      onSaved?.();
      onClose();
    } catch (error) {
      setServerError(error instanceof ApiRequestError ? error.message : 'Er is een onbekende fout opgetreden');
    }
  }

  const closesCurrentPackageInfo = currentPackage
    ? `Huidig pakket loopt dan t/m ${formatDutchDate(lastDayOfPreviousMonth(startDate))}`
    : null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="package-change-dialog-overlay">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="package-change-dialog"
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
      >
        <h2 className="text-lg font-medium text-gray-900 mb-4">Pakket wijzigen</h2>

        {serverError && (
          <div data-testid="package-change-server-error" className="mb-4 px-3 py-2 rounded-md bg-[#D64545]/10 text-[#D64545] text-sm">
            {serverError}
          </div>
        )}

        {/* Ingangsdatum */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="package-change-start-date">
            Ingangsdatum
          </label>
          <select
            id="package-change-start-date"
            data-testid="package-change-start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
          >
            {monthOptions.map((iso) => (
              <option key={iso} value={iso}>
                {formatDutchDate(iso)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Pakketten wisselen per maand.
            {closesCurrentPackageInfo && <> {closesCurrentPackageInfo}</>}
          </p>
        </div>

        {/* Kerntaken-regels */}
        <div className="space-y-3 mb-2">
          {rows.map((row, index) => (
            <div key={row.key} className="flex items-center gap-3" data-testid={`package-row-${index}`}>
              <div className="flex-1">
                <CoreTaskPicker
                  tasks={coreTasks ?? []}
                  value={row.coreTaskId}
                  onChange={(coreTaskId) => updateRow(row.key, { coreTaskId })}
                  excludeIds={excludeIds}
                  testId={`package-row-${index}-core-task-picker`}
                />
              </div>
              <PercentageStepper
                value={row.timePercentage}
                onChange={(timePercentage) => updateRow(row.key, { timePercentage })}
                testId={`package-row-${index}-stepper`}
              />
            </div>
          ))}
        </div>

        {/* Uitzonderingsroute */}
        {rows.length === DEFAULT_ROW_COUNT && (
          <button
            type="button"
            data-testid="add-4th-core-task-link"
            onClick={addExceptionRow}
            className="text-sm text-[#54949E] hover:underline mb-3"
          >
            4e kerntaak toevoegen (uitzondering)
          </button>
        )}
        {rows.length === DEFAULT_ROW_COUNT + 1 && (
          <button
            type="button"
            data-testid="add-5th-core-task-link"
            onClick={addExceptionRow}
            className="text-sm text-[#54949E] hover:underline mb-3"
          >
            5e kerntaak toevoegen (uitzondering)
          </button>
        )}

        {hasException && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="exception-motivation-textarea">
              Motivatie voor 4e/5e kerntaak (verplicht)
            </label>
            <textarea
              id="exception-motivation-textarea"
              data-testid="exception-motivation-textarea"
              value={exceptionMotivation}
              onChange={(e) => setExceptionMotivation(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
            />
            {saveAttempted && !exceptionMotivationValid && (
              <p className="mt-1 text-xs text-[#D64545]">Motivatie is verplicht.</p>
            )}
          </div>
        )}

        {/* Somindicator */}
        <div className="mb-6">
          {sumIsValid ? (
            <p data-testid="sum-indicator" className="text-sm text-[#B7D945] flex items-center gap-1">
              <span aria-hidden="true">&#10003;</span> 100%
            </p>
          ) : (
            <p
              data-testid="sum-indicator"
              className={`text-sm ${saveAttempted ? 'text-[#D64545]' : 'text-gray-500'}`}
            >
              {sum < MAX_PERCENTAGE ? `Nog ${MAX_PERCENTAGE - sum}% te verdelen` : `${sum - MAX_PERCENTAGE}% te veel toegewezen`}
            </p>
          )}
        </div>

        {/* Acties */}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="package-change-cancel"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 rounded-md border border-[#E2E8F0] hover:bg-gray-50"
          >
            Annuleren
          </button>
          <span className="relative group">
            {/*
              Bewust GEEN native `disabled` — een echt disabled element vuurt
              in geen enkele browser een click-event, waardoor een "poging tot
              opslaan" (UX-spec: "rood alléén bij poging tot opslaan") nooit
              zou kunnen plaatsvinden. In plaats daarvan: visueel/functioneel
              "zacht" disabled via `aria-disabled` + een vroege return in
              `handleSave` zolang !isValid — de klik blijft dus wél
              afvangbaar om `saveAttempted` te zetten en de rode
              validatie-guidance te tonen, zonder dat er echt wordt
              opgeslagen.
            */}
            <button
              type="button"
              data-testid="package-change-save"
              aria-disabled={!isValid || saveTaskPeriod.isPending}
              onClick={handleSave}
              title={!isValid ? missingReasons().join(' · ') : undefined}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md bg-[#2F5A7A] hover:bg-[#2F5A7A]/90 ${
                !isValid || saveTaskPeriod.isPending ? 'opacity-40 cursor-not-allowed' : ''
              }`}
            >
              Opslaan
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
